/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import {defineMessage, defineMessages} from 'react-intl'
import {noop} from './utils/utils'

// Global app defaults
export const DEFAULT_LOCALE = 'en-US'
export const DEFAULT_SITE_TITLE = 'Vilebrequin Official Website'
export const MAX_CACHE_AGE = 60 * 15 // 15 min
export const STALE_WHILE_REVALIDATE = 60 * 15 // 15 min

// Constants used for product searching.
export const DEFAULT_SEARCH_PARAMS = {limit: 25, offset: 0, refine: []}
export const DEFAULT_LIMIT_VALUES = [25, 50, 100] // Page sizes

//Constants for customer orders searching.
export const DEFAULT_ORDERS_SEARCH_PARAMS = {limit: 10, offset: 0, sort: 'best-matches', refine: []}

// Constants for Search Component
export const RECENT_SEARCH_LIMIT = 5
export const RECENT_SEARCH_KEY = 'recent-search-key'
export const RECENT_SEARCH_MIN_LENGTH = 3

// Constants for product list page
export const PRODUCT_LIST_IMAGE_VIEW_TYPE = 'large'
export const PRODUCT_LIST_SELECTABLE_ATTRIBUTE_ID = 'color'

// Constants for product tile page
export const PRODUCT_TILE_IMAGE_VIEW_TYPE = 'large'
export const PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID = 'color'

// Constants for the Homepage's Shop Products section.
export const HOME_SHOP_PRODUCTS_CATEGORY_ID = 'newarrivals'
export const HOME_SHOP_PRODUCTS_LIMIT = 10

// Constants for menu
export const CAT_MENU_DEFAULT_ROOT_CATEGORY = 'root'

// Color to use for the UI surrounding the page in browsers.
export const THEME_COLOR = '#0176D3'

export const FILTER_ACCORDION_SATE = 'filters-expanded-index'

export const INVALID_TOKEN_ERROR_MESSAGE = defineMessage({
    defaultMessage: 'Invalid token, please try again.',
    id: 'global.error.invalid_token'
})
export const FEATURE_UNAVAILABLE_ERROR_MESSAGE = defineMessage({
    defaultMessage: 'This feature is not currently available.',
    id: 'global.error.feature_unavailable'
})
export const CREATE_ACCOUNT_FIRST_ERROR_MESSAGE = defineMessage({
    defaultMessage:
        'This feature is not currently available. You must create an account to access this feature.',
    id: 'global.error.create_account'
})

export const GIFT_CARD_ALT = defineMessage({
    defaultMessage: 'Gift Card Image',
    id: 'giftcard.image'
})

export const SALES_TAX_LABEL = defineMessage({
    defaultMessage: 'Sales tax:',
    id: 'item_attributes.label.sales_tax'
})

export const DISCOUNT_AMOUNT_LABEL = defineMessage({
    defaultMessage: 'Discount amount:',
    id: 'item_attributes.label.discount.amount'
})

export const urlPartPositions = {
    PATH: 'path',
    QUERY_PARAM: 'query_param',
    NONE: 'none'
}

// Toast messages exist outside the scope of the base IntlProvider. This means
// that commonly used components that require localization cannot easily be
// extracted into a hook/helper. However, we can still extract the message
// definitions to a common location (here), so that each message is only defined
// once.

export const TOAST_MESSAGE_ADDED_TO_WISHLIST = defineMessage({
    id: 'global.info.added_to_wishlist',
    defaultMessage: '{quantity} {quantity, plural, one {item} other {items}} added to wishlist'
})

export const TOAST_MESSAGE_ALREADY_IN_WISHLIST = defineMessage({
    id: 'global.info.already_in_wishlist',
    defaultMessage: 'Item is already in wishlist'
})

export const TOAST_MESSAGE_REMOVED_ITEM_FROM_CART = defineMessage({
    defaultMessage: 'Item removed from cart',
    id: 'cart.info.removed_from_cart'
})

export const TOAST_ACTION_VIEW_WISHLIST = defineMessage({
    defaultMessage: 'View',
    id: 'global.link.added_to_wishlist.view_wishlist'
})

export const TOAST_MESSAGE_REMOVED_FROM_WISHLIST = defineMessage({
    id: 'global.info.removed_from_wishlist',
    defaultMessage: 'Item removed from wishlist'
})

// Constant to Enable Active Data
export const ACTIVE_DATA_ENABLED = true

export const REMOVE_UNAVAILABLE_CART_ITEM_DIALOG_CONFIG = {
    dialogTitle: defineMessage({
        defaultMessage: 'Items Unavailable',
        id: 'confirmation_modal.remove_cart_item.title.items_unavailable'
    }),
    confirmationMessage: defineMessage({
        defaultMessage:
            'Some items are no longer available online and will be removed from your cart.',
        id: 'confirmation_modal.remove_cart_item.message.need_to_remove_due_to_unavailability'
    }),
    primaryActionLabel: defineMessage({
        defaultMessage: 'Remove',
        id: 'confirmation_modal.remove_cart_item.action.remove'
    }),
    primaryActionAriaLabel: defineMessage({
        defaultMessage: 'Remove unavailable products',
        id: 'confirmation_modal.remove_cart_item.assistive_msg.remove'
    }),
    onPrimaryAction: noop
}

export const PRODUCT_ATTRIBUTES = {
    style: defineMessage({
        defaultMessage: 'Style',
        id: 'product_detail.attr.style'
    }),
    model: defineMessage({
        defaultMessage: 'Model',
        id: 'product_detail.attr.model'
    }),
    color: defineMessage({
        defaultMessage: 'Color',
        id: 'product_detail.attr.color'
    })
}

export const SUPPORTED_STORE_LOCATOR_COUNTRIES = [
    {
        countryCode: 'US',
        countryName: defineMessage({
            defaultMessage: 'United States',
            id: 'store_locator.dropdown.united_states'
        })
    },
    {
        countryCode: 'DE',
        countryName: defineMessage({
            defaultMessage: 'Germany',
            id: 'store_locator.dropdown.germany'
        })
    }
]

export const DEFAULT_STORE_LOCATOR_COUNTRY = {
    countryCode: 'DE',
    countryName: defineMessage({
        defaultMessage: 'Germany',
        id: 'store_locator.dropdown.germany'
    })
}
export const DEFAULT_STORE_LOCATOR_POSTAL_CODE = '10178'
export const STORE_LOCATOR_DISTANCE = 100
export const STORE_LOCATOR_NUM_STORES_PER_LOAD = 10
export const STORE_LOCATOR_DISTANCE_UNIT = 'km'
export const STORE_LOCATOR_IS_ENABLED = true

// Constants for shopper context
// Supported non-string field types used in SHOPPER_CONTEXT_SEARCH_PARAMS below.
// Only non-string types need to be identified using the "type" field.
// If no "type" field is present, the value will be parsed as a string by default.
export const SHOPPER_CONTEXT_FIELD_TYPES = {
    INT: 'int',
    DOUBLE: 'double',
    ARRAY: 'array'
}
export const SHOPPER_CONTEXT_SEARCH_PARAMS = {
    sourceCode: {paramName: 'sourceCode'},
    geoLocation: {
        city: {paramName: 'city'},
        country: {paramName: 'country'},
        countryCode: {paramName: 'countryCode'},
        latitude: {paramName: 'latitude', type: SHOPPER_CONTEXT_FIELD_TYPES.DOUBLE},
        longitude: {paramName: 'longitude', type: SHOPPER_CONTEXT_FIELD_TYPES.DOUBLE},
        metroCode: {paramName: 'metroCode'},
        postalCode: {paramName: 'postalCode'},
        region: {paramName: 'region'},
        regionCode: {paramName: 'regionCode'}
    },
    customQualifiers: {
        // Add custom qualifiers here
    },
    assignmentQualifiers: {
        // Add assignment qualifiers here
    }
}

// Constants for Login
export const LOGIN_TYPES = {
    PASSWORD: 'password',
    PASSWORDLESS: 'passwordless',
    SOCIAL: 'social'
}

// Constants for Password Reset
export const RESET_PASSWORD_LANDING_PATH = '/reset-password-landing'

// Constants for Passwordless Login
export const PASSWORDLESS_LOGIN_LANDING_PATH = '/passwordless-login-landing'

export const PASSWORDLESS_ERROR_MESSAGES = [
    /callback_uri doesn't match/i,
    /passwordless permissions error/i,
    /client secret is not provided/i
]

export const INVALID_TOKEN_ERROR = /invalid token/i

export const USER_NOT_FOUND_ERROR = /user not found/i

export const DEFAULT_DNT_STATE = false

// Generic messages START
export const GENERIC_MESSAGES = defineMessages({
    LOADING: {
        id: 'generic.loading',
        defaultMessage: 'Loading...',
        description: 'Generic loading message'
    },
    CAPTCHA_FAILED: {
        id: 'generic.captcha.failed',
        defaultMessage: 'Captcha verification failed',
        description: 'Generic captcha failed message'
    }
})
// Generic messages END

// Limit of products displayed on product search page: /search?q=
export const SEARCH_LIMIT_VALUE = 20

// Limit of products displayed on PLP for every level
export const SEARCH_PARAMS_LIMIT = 100

// Limit of products for laod more on PLP for every level
export const SEARCH_PARAM_LOAD_MORE_LIMIT = 40
// Default details of badge labels and the corresponding product custom properties that enable badges.
export const PRODUCT_BADGE_DETAILS = [
    {
        propertyName: 'c_isNew',
        label: defineMessage({
            id: 'product_tile.badge.label.new',
            defaultMessage: 'New'
        }),
        color: 'fullBlack'
    },
    {
        propertyName: 'c_isSale',
        label: defineMessage({
            id: 'product_tile.badge.label.sale',
            defaultMessage: 'Sale'
        }),
        color: 'fullBlack'
    },
    {
        propertyName: 'c_tag',
        propertyColorName: 'c_tagColor',
        propertyLinkName: 'c_tagLink',
        color: 'fullBlack'
    }
]

export const CUSTOM_HOME_TITLE = '🎉 Hello Extensible React Template!'

export const DISPATCH_COUNTRIES = [
    {
        continent: 'EUROPE',
        countries: [
            {code: 'AX', name: 'Aland Islands'},
            {code: 'AL', name: 'Albania'},
            {code: 'AD', name: 'Andorra'},
            {code: 'AT', name: 'Austria'},
            //{code: 'BY', name: 'Belarus'},
            {code: 'BE', name: 'Belgium'},
            {code: 'BA', name: 'Bosnia and Herzegovina'},
            {code: 'BG', name: 'Bulgaria'},
            {code: 'IC', name: 'Canary Islands'},
            {code: 'HR', name: 'Croatia'},
            {code: 'CY', name: 'Cyprus'},
            {code: 'CZ', name: 'Czech Republic'},
            {code: 'DK', name: 'Denmark'},
            {code: 'EE', name: 'Estonia'},
            //{code: 'FO', name: 'Faroe Islands'},
            {code: 'FI', name: 'Finland'},
            {code: 'FR', name: 'France'},
            {code: 'DE', name: 'Germany'},
            {code: 'GI', name: 'Gibraltar'},
            {code: 'GR', name: 'Greece'},
            //{code: 'GL', name: 'Greenland'},
            {code: 'GG', name: 'Guernsey'},
            {code: 'HU', name: 'Hungary'},
            {code: 'IS', name: 'Iceland'},
            {code: 'IE', name: 'Ireland'},
            {code: 'IM', name: 'Isle of Man'},
            {code: 'IT', name: 'Italy'},
            {code: 'JE', name: 'Jersey'},
            //{code: 'XK', name: 'Kosovo'},
            {code: 'LV', name: 'Latvia'},
            {code: 'LI', name: 'Liechtenstein'},
            {code: 'LT', name: 'Lithuania'},
            {code: 'LU', name: 'Luxembourg'},
            //{code: 'MK', name: 'Macedonia'},
            {code: 'MT', name: 'Malta'},
            //{code: 'MD', name: 'Moldova'},
            {code: 'MC', name: 'Monaco'},
            {code: 'ME', name: 'Montenegro'},
            {code: 'NL', name: 'Netherlands'},
            {code: 'NO', name: 'Norway'},
            {code: 'PL', name: 'Poland'},
            {code: 'PT', name: 'Portugal'},
            {code: 'RO', name: 'Romania'},
            {code: 'RU', name: 'Russia'},
            {code: 'SM', name: 'San Marino'},
            {code: 'RS', name: 'Serbia'},
            {code: 'SK', name: 'Slovakia'},
            {code: 'SI', name: 'Slovenia'},
            {code: 'ES', name: 'Spain'},
            //{code: 'SJ', name: 'Svalbard and Jan Mayen'},
            {code: 'SE', name: 'Sweden'},
            {code: 'CH', name: 'Switzerland'},
            //{code: 'UA', name: 'Ukraine'},
            {code: 'GB', name: 'United Kingdom'},
            {code: 'VA', name: 'Vatican'}
        ]
    },
    {
        continent: 'AMERICAS',
        countries: [
            {code: 'AI', name: 'Anguilla'},
            {code: 'AG', name: 'Antigua and Barbuda'},
            {code: 'AR', name: 'Argentina'},
            {code: 'AW', name: 'Aruba'},
            {code: 'BS', name: 'Bahamas'},
            {code: 'BB', name: 'Barbados'},
            {code: 'BZ', name: 'Belize'},
            {code: 'BM', name: 'Bermuda'},
            {code: 'BO', name: 'Bolivia'},
            //{code: 'BQ', name: 'Bonaire, Saint Eustatius and Saba'},
            {code: 'BR', name: 'Brazil'},
            {code: 'VG', name: 'British Virgin Islands'},
            {code: 'CA', name: 'Canada'},
            {code: 'KY', name: 'Cayman Islands'},
            {code: 'CL', name: 'Chile'},
            {code: 'CO', name: 'Colombia'},
            {code: 'CR', name: 'Costa Rica'},
            //{code: 'CU', name: 'Cuba'},
            {code: 'CW', name: 'Curacao'},
            {code: 'DM', name: 'Dominica'},
            {code: 'DO', name: 'Dominican Republic'},
            {code: 'EC', name: 'Ecuador'},
            {code: 'SV', name: 'El Salvador'},
            //{code: 'FK', name: 'Falkland Islands'},
            {code: 'GF', name: 'French Guiana'},
            {code: 'GD', name: 'Grenada'},
            {code: 'GP', name: 'Guadeloupe'},
            {code: 'GT', name: 'Guatemala'},
            {code: 'GY', name: 'Guyana'},
            {code: 'HT', name: 'Haiti'},
            {code: 'HN', name: 'Honduras'},
            {code: 'JM', name: 'Jamaica'},
            {code: 'MQ', name: 'Martinique'},
            {code: 'MX', name: 'Mexico'},
            {code: 'MS', name: 'Montserrat'},
            {code: 'NI', name: 'Nicaragua'},
            {code: 'PA', name: 'Panama'},
            {code: 'PY', name: 'Paraguay'},
            {code: 'PE', name: 'Peru'},
            //{code: 'PR', name: 'Puerto Rico'},
            {code: 'BL', name: 'Saint Barthelemy'},
            //{code: 'KN', name: 'Saint Kitts and Nevis'},
            {code: 'LC', name: 'Saint Lucia'},
            //{code: 'SX', name: 'Sint Maarten'},
            {code: 'MF', name: 'Saint Martin'},
            //{code: 'PM', name: 'Saint Pierre and Miquelon'},
            {code: 'VC', name: 'Saint Vincent and the Grenadines'},
            //{code: 'SR', name: 'Suriname'},
            {code: 'TT', name: 'Trinidad and Tobago'},
            {code: 'TC', name: 'Turks and Caicos Islands'},
            //{code: 'VI', name: 'U.S. Virgin Islands'},
            {code: 'US', name: 'United States'},
            {code: 'UY', name: 'Uruguay'}
            //{code: 'GS', name: 'South Georgia and the South Sandwich Islands'}
            //{code: 'VE', name: 'Venezuela'}
        ]
    },
    {
        continent: 'AFRIQUE',
        countries: [
            //{code: 'DZ', name: 'Algeria'},
            {code: 'AO', name: 'Angola'},
            {code: 'BJ', name: 'Benin'},
            {code: 'BW', name: 'Botswana'},
            //{code: 'BV', name: 'Bouvet Island'},
            {code: 'BF', name: 'Burkina Faso'},
            {code: 'BI', name: 'Burundi'},
            //{code: 'CV', name: 'Cape Verde'},
            {code: 'CM', name: 'Cameroon'},
            //{code: 'CF', name: 'Central African Republic'},
            {code: 'TD', name: 'Chad'},
            //{code: 'KM', name: 'Comoros'},
            //{code: 'CD', name: 'Democratic Republic of the Congo'},
            //{code: 'DJ', name: 'Djibouti'},
            {code: 'EG', name: 'Egypt'},
            {code: 'GQ', name: 'Equatorial Guinea'},
            {code: 'ER', name: 'Eritrea'},
            {code: 'ET', name: 'Ethiopia'},
            {code: 'GA', name: 'Gabon'},
            {code: 'GM', name: 'Gambia'},
            {code: 'GH', name: 'Ghana'},
            //{code: 'GN', name: 'Guinea'},
            //{code: 'GW', name: 'Guinea-Bissau'},
            //{code: 'CI', name: 'Ivory Coast'},
            {code: 'KE', name: 'Kenya'},
            {code: 'LS', name: 'Lesotho'},
            {code: 'LR', name: 'Liberia'},
            //{code: 'LY', name: 'Libya'},
            {code: 'MG', name: 'Madagascar'},
            {code: 'MW', name: 'Malawi'},
            {code: 'ML', name: 'Mali'},
            //{code: 'MR', name: 'Mauritania'},
            {code: 'MU', name: 'Mauritius'},
            {code: 'YT', name: 'Mayotte'},
            {code: 'MA', name: 'Morocco'},
            {code: 'MZ', name: 'Mozambique'},
            {code: 'NA', name: 'Namibia'},
            {code: 'NE', name: 'Niger'},
            {code: 'NG', name: 'Nigeria'},
            {code: 'CG', name: 'Republic of the Congo'},
            {code: 'RE', name: 'Reunion'},
            {code: 'RW', name: 'Rwanda'},
            //{code: 'SH', name: 'Saint Helena, Ascension and Tristan da Cunha'},
            //{code: 'ST', name: 'Sao Tome and Principe'},
            {code: 'SN', name: 'Senegal'},
            //{code: 'SC', name: 'Seychelles'},
            {code: 'SL', name: 'Sierra Leone'},
            //{code: 'SO', name: 'Somalia'},
            {code: 'ZA', name: 'South Africa'},
            //{code: 'SS', name: 'South Sudan'},
            //{code: 'SD', name: 'Sudan'},
            {code: 'SZ', name: 'Swaziland'},
            {code: 'TZ', name: 'Tanzania'},
            {code: 'TG', name: 'Togo'},
            //{code: 'TN', name: 'Tunisia'},
            {code: 'UG', name: 'Uganda'},
            //{code: 'EH', name: 'Western Sahara'},
            {code: 'ZM', name: 'Zambia'},
            {code: 'ZW', name: 'Zimbabwe'}
        ]
    },
    {
        continent: 'ASIA',
        countries: [
            //{code: 'AF', name: 'Afghanistan'},
            //{code: 'AM', name: 'Armenia'},
            //{code: 'AZ', name: 'Azerbaijan'},
            {code: 'BD', name: 'Bangladesh'},
            {code: 'BT', name: 'Bhutan'},
            //{code: 'IO', name: 'British Indian Ocean Territory'},
            //{code: 'TF', name: 'French Southern Territories'},
            {code: 'BN', name: 'Brunei Darussalam'},
            {code: 'KH', name: 'Cambodia'},
            {code: 'CN', name: 'China'},
            {code: 'TL', name: 'East Timor'},
            {code: 'HK', name: 'Hong Kong'},
            {code: 'IN', name: 'India'},
            {code: 'ID', name: 'Indonesia'},
            //{code: 'IR', name: 'Iran'},
            //{code: 'IQ', name: 'Iraq'},
            //{code: 'KZ', name: 'Kazakhstan'},
            //{code: 'KG', name: 'Kyrgyzstan'},
            //{code: 'TJ', name: 'Tajikistan'},
            //{code: 'TM', name: 'Turkmenistan'},
            //{code: 'UZ', name: 'Uzbekistan'},
            {code: 'JP', name: 'Japan'},
            {code: 'LA', name: 'Laos'},
            {code: 'MO', name: 'Macau'},
            {code: 'MY', name: 'Malaysia'},
            {code: 'MV', name: 'Maldives'},
            {code: 'MN', name: 'Mongolia'},
            //{code: 'MM', name: 'Myanmar'},
            //{code: 'NP', name: 'Nepal'},
            //{code: 'KP', name: 'North Korea'},
            {code: 'PK', name: 'Pakistan'},
            {code: 'PH', name: 'Philippines'},
            {code: 'SG', name: 'Singapore'},
            {code: 'KR', name: 'South Korea'},
            {code: 'LK', name: 'Sri Lanka'},
            {code: 'TW', name: 'Taiwan'},
            {code: 'TH', name: 'Thailand'},
            {code: 'VN', name: 'Vietnam'}
        ]
    },
    {
        continent: 'MIDDLE EAST',
        countries: [
            {code: 'BH', name: 'Bahrain'},
            {code: 'IL', name: 'Israel'},
            {code: 'JO', name: 'Jordan'},
            {code: 'KW', name: 'Kuwait'},
            //{code: 'LB', name: 'Lebanon'},
            {code: 'OM', name: 'Oman'},
            //{code: 'PS', name: 'Palestinian Territory'},
            {code: 'QA', name: 'Qatar'},
            {code: 'SA', name: 'Saudi Arabia'},
            {code: 'TR', name: 'Turkey'},
            //{code: 'GE', name: 'Georgia'},
            //{code: 'SY', name: 'Syria'},
            //{code: 'YE', name: 'Yemen'},
            {code: 'AE', name: 'United Arab Emirates'}
        ]
    },
    {
        continent: 'OCEANIA',
        countries: [
            //{code: 'AS', name: 'American Samoa'},
            {code: 'AU', name: 'Australia'},
            //{code: 'CX', name: 'Christmas Island'},
            //{code: 'CC', name: 'Cocos Islands'},
            //{code: 'CK', name: 'Cook Islands'},
            //{code: 'HM', name: 'Heard Island and McDonald Islands'},
            {code: 'FJ', name: 'Fiji'},
            {code: 'PF', name: 'French Polynesia'},
            //{code: 'GU', name: 'Guam'},
            //{code: 'KI', name: 'Kiribati'},
            //{code: 'MH', name: 'Marshall Islands'},
            //{code: 'FM', name: 'Micronesia'},
            //{code: 'NR', name: 'Nauru'},
            //{code: 'NC', name: 'New Caledonia'},
            {code: 'NZ', name: 'New Zealand'},
            //{code: 'NU', name: 'Niue'},
            //{code: 'NF', name: 'Norfolk Island'},
            //{code: 'MP', name: 'Northern Mariana Islands'},
            //{code: 'PW', name: 'Palau'},
            {code: 'PG', name: 'Papua New Guinea'},
            //{code: 'WS', name: 'Samoa'},
            //{code: 'SB', name: 'Solomon Islands'},
            {code: 'TK', name: 'Tokelau'},
            //{code: 'TO', name: 'Tonga'},
            //{code: 'TV', name: 'Tuvalu'},
            //{code: 'UM', name: 'United States Minor Outlying Islands'},
            {code: 'VU', name: 'Vanuatu'},
            {code: 'WF', name: 'Wallis and Futuna'}
        ]
    }
]

//Constants for Size Guide on PDPs
export const UNIT_CM = 'CM'
export const UNIT_INCH = 'INCH'
//End Constants for Size Guide on PDPs

export const COUNTRIES_CURRENCY = [
    {id: 'AD', currency: 'EUR'},
    {id: 'AT', currency: 'EUR'},
    {id: 'AX', currency: 'EUR'},
    {id: 'BE', currency: 'EUR'},
    {id: 'BG', currency: 'EUR'},
    {id: 'CH', currency: 'CHF'},
    {id: 'CW', currency: 'EUR'},
    {id: 'CY', currency: 'EUR'},
    {id: 'CZ', currency: 'EUR'},
    {id: 'DE', currency: 'EUR'},
    {id: 'DK', currency: 'EUR'},
    {id: 'EE', currency: 'EUR'},
    {id: 'ES', currency: 'EUR'},
    {id: 'FI', currency: 'EUR'},
    {id: 'FR', currency: 'EUR'},
    {id: 'GR', currency: 'EUR'},
    {id: 'HR', currency: 'EUR'},
    {id: 'HU', currency: 'EUR'},
    {id: 'IC', currency: 'EUR'},
    {id: 'IE', currency: 'EUR'},
    {id: 'IT', currency: 'EUR'},
    {id: 'LT', currency: 'EUR'},
    {id: 'LU', currency: 'EUR'},
    {id: 'LV', currency: 'EUR'},
    {id: 'MC', currency: 'EUR'},
    {id: 'MT', currency: 'EUR'},
    {id: 'NL', currency: 'EUR'},
    {id: 'PT', currency: 'EUR'},
    {id: 'RO', currency: 'EUR'},
    {id: 'SE', currency: 'EUR'},
    {id: 'SI', currency: 'EUR'},
    {id: 'SK', currency: 'EUR'},
    {id: 'VA', currency: 'EUR'},
    {id: 'AE', currency: 'EUR'},
    {id: 'AG', currency: 'EUR'},
    {id: 'AI', currency: 'EUR'},
    {id: 'AL', currency: 'EUR'},
    {id: 'AO', currency: 'EUR'},
    {id: 'AR', currency: 'EUR'},
    {id: 'AW', currency: 'EUR'},
    {id: 'BA', currency: 'EUR'},
    {id: 'BB', currency: 'EUR'},
    {id: 'BD', currency: 'EUR'},
    {id: 'BF', currency: 'EUR'},
    {id: 'BH', currency: 'EUR'},
    {id: 'BJ', currency: 'EUR'},
    {id: 'BL', currency: 'EUR'},
    {id: 'BM', currency: 'EUR'},
    {id: 'BN', currency: 'EUR'},
    {id: 'BO', currency: 'EUR'},
    {id: 'BR', currency: 'EUR'},
    {id: 'BT', currency: 'EUR'},
    {id: 'BW', currency: 'EUR'},
    {id: 'BZ', currency: 'EUR'},
    {id: 'CG', currency: 'EUR'},
    {id: 'CL', currency: 'EUR'},
    {id: 'CO', currency: 'EUR'},
    {id: 'CR', currency: 'EUR'},
    {id: 'DM', currency: 'EUR'},
    {id: 'DO', currency: 'EUR'},
    {id: 'EC', currency: 'EUR'},
    {id: 'EG', currency: 'EUR'},
    {id: 'ER', currency: 'EUR'},
    {id: 'ET', currency: 'EUR'},
    {id: 'FJ', currency: 'EUR'},
    {id: 'GA', currency: 'EUR'},
    {id: 'GD', currency: 'EUR'},
    {id: 'GF', currency: 'EUR'},
    {id: 'GI', currency: 'EUR'},
    {id: 'GM', currency: 'EUR'},
    {id: 'GP', currency: 'EUR'},
    {id: 'GQ', currency: 'EUR'},
    {id: 'GT', currency: 'EUR'},
    {id: 'GY', currency: 'EUR'},
    {id: 'HN', currency: 'EUR'},
    {id: 'ID', currency: 'EUR'},
    {id: 'IL', currency: 'EUR'},
    {id: 'IS', currency: 'EUR'},
    {id: 'JM', currency: 'EUR'},
    {id: 'JO', currency: 'EUR'},
    {id: 'KE', currency: 'EUR'},
    {id: 'KH', currency: 'EUR'},
    {id: 'KR', currency: 'EUR'},
    {id: 'KW', currency: 'EUR'},
    {id: 'KY', currency: 'EUR'},
    {id: 'LA', currency: 'EUR'},
    {id: 'LC', currency: 'EUR'},
    {id: 'LK', currency: 'EUR'},
    {id: 'LS', currency: 'EUR'},
    {id: 'MA', currency: 'EUR'},
    {id: 'MG', currency: 'EUR'},
    {id: 'ME', currency: 'EUR'},
    {id: 'ML', currency: 'EUR'},
    {id: 'MN', currency: 'EUR'},
    {id: 'MQ', currency: 'EUR'},
    {id: 'MS', currency: 'EUR'},
    {id: 'MU', currency: 'EUR'},
    {id: 'MV', currency: 'EUR'},
    {id: 'MW', currency: 'EUR'},
    {id: 'MZ', currency: 'EUR'},
    {id: 'NE', currency: 'EUR'},
    {id: 'NG', currency: 'EUR'},
    {id: 'NI', currency: 'EUR'},
    {id: 'NO', currency: 'EUR'},
    {id: 'NZ', currency: 'EUR'},
    {id: 'OM', currency: 'EUR'},
    {id: 'PA', currency: 'EUR'},
    {id: 'PE', currency: 'EUR'},
    {id: 'PG', currency: 'EUR'},
    {id: 'PH', currency: 'EUR'},
    {id: 'PK', currency: 'EUR'},
    {id: 'PL', currency: 'EUR'},
    {id: 'PY', currency: 'EUR'},
    {id: 'QA', currency: 'EUR'},
    {id: 'RE', currency: 'EUR'},
    {id: 'RS', currency: 'EUR'},
    {id: 'RW', currency: 'EUR'},
    {id: 'SA', currency: 'EUR'},
    {id: 'SM', currency: 'EUR'},
    {id: 'SV', currency: 'EUR'},
    {id: 'SZ', currency: 'EUR'},
    {id: 'TC', currency: 'EUR'},
    {id: 'TD', currency: 'EUR'},
    {id: 'TG', currency: 'EUR'},
    {id: 'TH', currency: 'EUR'},
    {id: 'TK', currency: 'EUR'},
    {id: 'TL', currency: 'EUR'},
    {id: 'TT', currency: 'EUR'},
    {id: 'TW', currency: 'EUR'},
    {id: 'TZ', currency: 'EUR'},
    {id: 'UG', currency: 'EUR'},
    {id: 'UY', currency: 'EUR'},
    {id: 'VC', currency: 'EUR'},
    {id: 'VG', currency: 'EUR'},
    {id: 'VN', currency: 'EUR'},
    {id: 'VU', currency: 'EUR'},
    {id: 'ZA', currency: 'EUR'},
    {id: 'ZM', currency: 'EUR'},
    {id: 'AU', currency: 'AUD'},
    {id: 'CA', currency: 'CAD'},
    {id: 'CN', currency: 'CNY'},
    {id: 'GB', currency: 'GBP'},
    {id: 'GG', currency: 'GBP'},
    {id: 'JE', currency: 'GBP'},
    {id: 'HK', currency: 'HKD'},
    {id: 'JP', currency: 'JPY'},
    {id: 'LI', currency: 'CHF'},
    {id: 'MO', currency: 'MOP'},
    {id: 'MX', currency: 'MXN'},
    {id: 'SG', currency: 'SGD'},
    {id: 'US', currency: 'USD'},
    {id: 'MY', currency: 'EUR'},
    {id: 'GH', currency: 'EUR'},
    {id: 'HT', currency: 'EUR'},
    {id: 'PF', currency: 'EUR'},
    {id: 'WF', currency: 'EUR'},
    {id: 'IN', currency: 'EUR'},
    {id: 'ZW', currency: 'EUR'},
    {id: 'SL', currency: 'EUR'},
    {id: 'SN', currency: 'EUR'},
    {id: 'NA', currency: 'EUR'},
    {id: 'LR', currency: 'EUR'},
    {id: 'CM', currency: 'EUR'},
    {id: 'BI', currency: 'EUR'},
    {id: 'YT', currency: 'EUR'},
    {id: 'MF', currency: 'EUR'},
    {id: 'IM', currency: 'GBP'},
    {id: 'BS', currency: 'EUR'}
    // {id: 'AQ', currency: ''},
    // {id: 'BQ', currency: ''},
    // {id: 'CX', currency: ''},
    // {id: 'CC', currency: ''},
    // {id: 'NC', currency: ''},
    // {id: 'PS', currency: ''},
    // {id: 'PR', currency: ''},
    // {id: 'KN', currency: ''},
    // {id: 'SC', currency: ''},
    // {id: 'SX', currency: ''},
    // {id: 'GS', currency: ''},
    // {id: 'SJ', currency: ''},
    // {id: 'TR', currency: ''},
    // {id: 'VI', currency: ''}
]

/**
 * A mapping of currency codes to their respective symbols.
 *
 * This constant provides a quick reference for displaying currency symbols
 * based on the currency code. It includes common currencies used in various
 * regions around the world.
 */
export const CURRENCY_SIGNS = {
    EUR: '€', // Euro
    CHF: 'CHF', // Swiss Franc
    USD: '$', // US Dollar
    NOK: 'kr', // Norwegian Krone
    NZD: 'NZ$', // New Zealand Dollar
    AUD: 'A$', // Australian Dollar
    CAD: 'C$', // Canadian Dollar
    CNY: '¥', // Chinese Yuan
    GBP: '£', // British Pound
    HKD: 'HK$', // Hong Kong Dollar
    JPY: '¥', // Japanese Yen
    MOP: 'MOP$', // Macanese Pataca
    MXN: 'MX$', // Mexican Peso
    SGD: 'S$' // Singapore Dollar
}

/*
 * Map of country codes to language codes
 * Used to determine the language to display for a given country
 */
export const COUNTRY_TO_LANGUAGE_MAP = {
    // Europe
    AX: 'sv',
    AL: 'sq',
    AD: 'ca',
    AT: 'de',
    BE: 'nl',
    BA: 'bs',
    BG: 'bg',
    HR: 'hr',
    CY: 'el',
    CZ: 'cs',
    DK: 'da',
    EE: 'et',
    FI: 'fi',
    FR: 'fr',
    DE: 'de',
    GI: 'en',
    GR: 'el',
    GG: 'en',
    HU: 'hu',
    IS: 'is',
    IE: 'en',
    IM: 'en',
    IT: 'it',
    JE: 'en',
    LV: 'lv',
    LI: 'de',
    LT: 'lt',
    LU: 'fr',
    MT: 'mt',
    MC: 'fr',
    ME: 'sr',
    NL: 'nl',
    NO: 'no',
    PL: 'pl',
    PT: 'pt',
    RO: 'ro',
    RU: 'ru',
    SM: 'it',
    RS: 'sr',
    SK: 'sk',
    SI: 'sl',
    ES: 'es',
    SE: 'sv',
    CH: 'de',
    GB: 'en',
    VA: 'it',

    // Americas
    AI: 'en',
    AG: 'en',
    AR: 'es',
    AW: 'nl',
    BS: 'en',
    BB: 'en',
    BZ: 'en',
    BM: 'en',
    BO: 'es',
    BR: 'pt',
    VG: 'en',
    CA: 'en',
    KY: 'en',
    CL: 'es',
    CO: 'es',
    CR: 'es',
    CW: 'nl',
    DM: 'en',
    DO: 'es',
    EC: 'es',
    SV: 'es',
    GF: 'fr',
    GD: 'en',
    GP: 'fr',
    GT: 'es',
    GY: 'en',
    HT: 'fr',
    HN: 'es',
    JM: 'en',
    MQ: 'fr',
    MX: 'es',
    MS: 'en',
    NI: 'es',
    PA: 'es',
    PY: 'es',
    PE: 'es',
    BL: 'fr',
    LC: 'en',
    MF: 'fr',
    VC: 'en',
    TT: 'en',
    TC: 'en',
    US: 'en',
    UY: 'es',

    // Africa
    AO: 'pt',
    BJ: 'fr',
    BW: 'en',
    BF: 'fr',
    BI: 'fr',
    CM: 'fr',
    TD: 'fr',
    EG: 'ar',
    GQ: 'es',
    ER: 'ti',
    ET: 'am',
    GA: 'fr',
    GM: 'en',
    GH: 'en',
    KE: 'en',
    LS: 'en',
    LR: 'en',
    MG: 'fr',
    MW: 'en',
    ML: 'fr',
    MU: 'en',
    YT: 'fr',
    MA: 'ar',
    MZ: 'pt',
    NA: 'en',
    NE: 'fr',
    NG: 'en',
    CG: 'fr',
    RE: 'fr',
    RW: 'rw',
    SN: 'fr',
    SL: 'en',
    ZA: 'en',
    SZ: 'en',
    TZ: 'sw',
    TG: 'fr',
    UG: 'en',
    ZM: 'en',
    ZW: 'en',

    // Asia
    BH: 'ar',
    BD: 'bn',
    BT: 'dz',
    BN: 'ms',
    KH: 'km',
    CN: 'zh',
    TL: 'pt',
    HK: 'zh',
    IN: 'hi',
    ID: 'id',
    JP: 'ja',
    KW: 'ar',
    LA: 'lo',
    MO: 'zh',
    MY: 'ms',
    MV: 'dv',
    MN: 'mn',
    OM: 'ar',
    PK: 'ur',
    PH: 'en',
    QA: 'ar',
    SA: 'ar',
    SG: 'en',
    KR: 'ko',
    LK: 'si',
    TW: 'zh',
    TH: 'th',
    AE: 'ar',
    VN: 'vi',

    // Middle East
    IL: 'he',
    JO: 'ar',
    TR: 'tr',

    // Oceania
    AU: 'en',
    FJ: 'en',
    PF: 'fr',
    NZ: 'en',
    PG: 'en',
    TK: 'en',
    VU: 'en',
    WF: 'fr'
}

// Age limits for legal age validation
export const LEGAL_AGE_EU = 15
export const LEGAL_AGE_US = 16

export const REGEX = {
    EMAIL: /^[\w%+-]+(?:\.[\w%+-]+)*@[\w-]+(?:\.[\w-]+)*\.[\w]{2,}$/,
    PHONE: /^\+(\d+)\s(.+)$/
}

export const cssColorGroups = {
    beige: '#d3bca9',
    black: '#000000',
    blue: '#4089c0',
    brown: '#8e6950',
    green: '#88c290',
    grey: '#919191',
    gray: '#919191',
    silver: '#c0c0c0',
    navy: '#000080',
    orange: '#f4995c',
    pink: '#f5a0ca',
    purple: '#9873b9',
    red: '#df5b5b',
    white: '#FFFFFF',
    yellow: '#fbe85a',
    gold: '#ffd700',
    miscellaneous: 'linear-gradient(to right, orange , yellow, green, cyan, blue, violet)'
}

export const OPTIONAL_INDICATOR_TEXT = defineMessage({
    defaultMessage: '(Optional)',
    id: 'field.indicator.optional'
})

export const SELECT_OPTION_TEXT = defineMessage({
    defaultMessage: 'Select an option...',
    id: 'field.select.select_option'
})

export const HOME_BREADCRUMB = defineMessage({
    defaultMessage: 'Vilebrequin',
    id: 'breadcrumb.home'
})

export const PRODUCT_IMAGE_TYPE = 'front'
export const PRODUCT_TRANSPARENT_BACKGROUND_IMAGE_TYPE = 'frontPNG'

// Magazine
// Warning : The blog folder must have the same id than the blog home page
export const BLOG_FOLDER_ID = 'blog'
export const MAGAZINE_HOME_PAGETYPE = 'magazineHome'
export const MAGAZINE_CATEGORY_PAGETYPE = 'magazineCategoryHome'
export const MAGAZINE_ARTICLE_PAGETYPE = 'magazineArticle'
export const MAGAZINE_NB_ARTICLES_PER_PAGE = 120
export const MAGAZINE_NB_ARTICLES_INITIAL = 14

export const KEY_CODES = {
    ENTER: 13
}

// "UNA" is a specific product type.
// For products where the custom field "orliweb_stat03_code_marche" is not equal to "UNA",
// this constant defines the default order of view types.
export const viewTypesOrderWithoutUNA = [
    'lifestyle',
    'video',
    'frontworn',
    'backworn',
    'supp1',
    'supp2',
    'supp3',
    'supp4',
    'supp5',
    'front',
    'back',
    'look1',
    'look2',
    'look3'
]

// "UNA" is a specific product type. For products with the custom field "orliweb_stat03_code_marche"
// equal to "UNA", the display order is slightly different. This constant defines the specific order
// of view types for these products.
export const viewTypesOrderUNA = [
    'frontworn',
    'video',
    'lifestyle',
    'frontworn-men',
    'frontworn-women',
    'backworn-men',
    'backworn-women',
    'supp1',
    'supp2',
    'supp3',
    'supp4',
    'supp5',
    'front',
    'back',
    'look1',
    'look2',
    'look3'
]

export const PAYMENT_METHODS = {
    ADYEN_COMPONENT: 'AdyenComponent',
    CREDIT_CARD: 'CREDIT_CARD',
    GIFT_CERTIFICATE: 'GIFT_CERTIFICATE'
}

export const PAYMENT_METHOD_TYPES = {
    GIFT_CARD: 'giftcard'
}

export const RESULT_CODES = {
    AUTHORISED: 'Authorised',
    CANCELLED: 'Cancelled',
    CHALLENGESHOPPER: 'ChallengeShopper',
    ERROR: 'Error',
    IDENTIFYSHOPPER: 'IdentifyShopper',
    PENDING: 'Pending',
    PRESENTTOSHOPPER: 'PresentToShopper',
    RECEIVED: 'Received',
    REDIRECTSHOPPER: 'RedirectShopper',
    REFUSED: 'Refused'
}

export const BLOCKED_PAYMENT_METHODS = [PAYMENT_METHOD_TYPES.GIFT_CARD]

export const SHOPPER_INTERACTIONS = {
    CONT_AUTH: 'ContAuth',
    ECOMMERCE: 'Ecommerce'
}

// only countries supported are listed, other are either excluded Zonos, excluded DHL, excluded because of DDU only customs, excluded because of UN sanctions
export const SHIPPING_COUNTRY_CODES = [
    {value: 'AX', label: 'Aland Islands'},
    {value: 'AL', label: 'Albania'},
    {value: 'AD', label: 'Andorra'},
    {value: 'AO', label: 'Angola'},
    {value: 'AI', label: 'Anguilla'},
    {value: 'AG', label: 'Antigua and Barbuda'},
    {value: 'AR', label: 'Argentina'},
    {value: 'AW', label: 'Aruba'},
    {value: 'AU', label: 'Australia'},
    {value: 'AT', label: 'Austria'},
    {value: 'BS', label: 'Bahamas'},
    {value: 'BH', label: 'Bahrain'},
    {value: 'BD', label: 'Bangladesh'},
    {value: 'BB', label: 'Barbados'},
    {value: 'BE', label: 'Belgium'},
    {value: 'BZ', label: 'Belize'},
    {value: 'BJ', label: 'Benin'},
    {value: 'BM', label: 'Bermuda'},
    {value: 'BT', label: 'Bhutan'},
    {value: 'BO', label: 'Bolivia'},
    {value: 'BA', label: 'Bosnia and Herzegovina'},
    {value: 'BW', label: 'Botswana'},
    {value: 'BR', label: 'Brazil'},
    {value: 'VG', label: 'British Virgin Islands'},
    {value: 'BN', label: 'Brunei Darussalam'},
    {value: 'BG', label: 'Bulgaria'},
    {value: 'BF', label: 'Burkina Faso'},
    {value: 'BI', label: 'Burundi'},
    {value: 'KH', label: 'Cambodia'},
    {value: 'CM', label: 'Cameroon'},
    {value: 'CA', label: 'Canada'},
    {value: 'IC', label: 'Canary Island'},
    {value: 'KY', label: 'Cayman Islands'},
    {value: 'TD', label: 'Chad'},
    {value: 'CL', label: 'Chile'},
    {value: 'CN', label: 'China'},
    {value: 'CO', label: 'Colombia'},
    {value: 'CR', label: 'Costa Rica'},
    {value: 'HR', label: 'Croatia'},
    {value: 'CW', label: 'Curacao'},
    {value: 'CY', label: 'Cyprus'},
    {value: 'CZ', label: 'Czech Republic'},
    {value: 'DK', label: 'Denmark'},
    {value: 'DM', label: 'Dominica'},
    {value: 'DO', label: 'Dominican Republic'},
    {value: 'TL', label: 'East Timor'},
    {value: 'EC', label: 'Ecuador'},
    {value: 'EG', label: 'Egypt'},
    {value: 'SV', label: 'El Salvador'},
    {value: 'GQ', label: 'Equatorial Guinea'},
    {value: 'ER', label: 'Eritrea'},
    {value: 'EE', label: 'Estonia'},
    {value: 'ET', label: 'Ethiopia'},
    {value: 'FJ', label: 'Fiji'},
    {value: 'FI', label: 'Finland'},
    {value: 'FR', label: 'France'},
    {value: 'GF', label: 'French Guiana'},
    {value: 'PF', label: 'French Polynesia'},
    {value: 'GA', label: 'Gabon'},
    {value: 'GM', label: 'Gambia'},
    {value: 'DE', label: 'Germany'},
    {value: 'GH', label: 'Ghana'},
    {value: 'GI', label: 'Gibraltar'},
    {value: 'GR', label: 'Greece'},
    {value: 'GD', label: 'Grenada'},
    {value: 'GP', label: 'Guadeloupe'},
    {value: 'GT', label: 'Guatemala'},
    {value: 'GG', label: 'Guernsey'},
    {value: 'GY', label: 'Guyana'},
    {value: 'HT', label: 'Haiti'},
    {value: 'HN', label: 'Honduras'},
    {value: 'HK', label: 'Hong Kong'},
    {value: 'HU', label: 'Hungary'},
    {value: 'IS', label: 'Iceland'},
    {value: 'IN', label: 'India'},
    {value: 'ID', label: 'Indonesia'},
    {value: 'IE', label: 'Ireland'},
    {value: 'IM', label: 'Isle of Man'},
    {value: 'IL', label: 'Israel'},
    {value: 'IT', label: 'Italy'},
    {value: 'JM', label: 'Jamaica'},
    {value: 'JP', label: 'Japan'},
    {value: 'JE', label: 'Jersey'},
    {value: 'JO', label: 'Jordan'},
    {value: 'KE', label: 'Kenya'},
    {value: 'KW', label: 'Kuwait'},
    {value: 'LA', label: 'Laos'},
    {value: 'LV', label: 'Latvia'},
    {value: 'LS', label: 'Lesotho'},
    {value: 'LR', label: 'Liberia'},
    {value: 'LI', label: 'Liechtenstein'},
    {value: 'LT', label: 'Lithuania'},
    {value: 'LU', label: 'Luxembourg'},
    {value: 'MO', label: 'Macau'},
    {value: 'MG', label: 'Madagascar'},
    {value: 'MW', label: 'Malawi'},
    {value: 'MY', label: 'Malaysia'},
    {value: 'MV', label: 'Maldives'},
    {value: 'ML', label: 'Mali'},
    {value: 'MT', label: 'Malta'},
    {value: 'MQ', label: 'Martinique'},
    {value: 'MU', label: 'Mauritius'},
    {value: 'YT', label: 'Mayotte'},
    {value: 'MX', label: 'Mexico'},
    {value: 'MC', label: 'Monaco'},
    {value: 'MN', label: 'Mongolia'},
    {value: 'MS', label: 'Montserrat'},
    {value: 'MA', label: 'Morocco'},
    {value: 'MZ', label: 'Mozambique'},
    {value: 'NA', label: 'Namibia'},
    {value: 'NL', label: 'Netherlands'},
    {value: 'NZ', label: 'New Zealand'},
    {value: 'NI', label: 'Nicaragua'},
    {value: 'NE', label: 'Niger'},
    {value: 'NG', label: 'Nigeria'},
    {value: 'NO', label: 'Norway'},
    {value: 'OM', label: 'Oman'},
    {value: 'PK', label: 'Pakistan'},
    {value: 'PA', label: 'Panama'},
    {value: 'PG', label: 'Papua New Guinea'},
    {value: 'PY', label: 'Paraguay'},
    {value: 'PE', label: 'Peru'},
    {value: 'PH', label: 'Philippines'},
    {value: 'PL', label: 'Poland'},
    {value: 'PT', label: 'Portugal'},
    {value: 'QA', label: 'Qatar'},
    {value: 'CG', label: 'Republic of the Congo'},
    {value: 'RE', label: 'Reunion'},
    {value: 'RO', label: 'Romania'},
    {value: 'RU', label: 'Russia'},
    {value: 'RW', label: 'Rwanda'},
    {value: 'BL', label: 'Saint Barthelemy'},
    {value: 'LC', label: 'Saint Lucia'},
    {value: 'MF', label: 'Saint Martin'},
    {value: 'VC', label: 'Saint Vincent and the Grenadines'},
    {value: 'SM', label: 'San Marino'},
    {value: 'SA', label: 'Saudi Arabia'},
    {value: 'SN', label: 'Senegal'},
    {value: 'RS', label: 'Serbia'},
    {value: 'SL', label: 'Sierra Leone'},
    {value: 'SG', label: 'Singapore'},
    {value: 'SK', label: 'Slovakia'},
    {value: 'SI', label: 'Slovenia'},
    {value: 'ZA', label: 'South Africa'},
    {value: 'KR', label: 'South Korea'},
    {value: 'ES', label: 'Spain'},
    {value: 'LK', label: 'Sri Lanka'},
    {value: 'SZ', label: 'Swaziland'},
    {value: 'SE', label: 'Sweden'},
    {value: 'CH', label: 'Switzerland'},
    {value: 'TW', label: 'Taiwan'},
    {value: 'TZ', label: 'Tanzania'},
    {value: 'TH', label: 'Thailand'},
    {value: 'TG', label: 'Togo'},
    {value: 'TK', label: 'Tokelau'},
    {value: 'TT', label: 'Trinidad and Tobago'},
    {value: 'TR', label: 'Turkey'},
    {value: 'TC', label: 'Turks and Caicos Islands'},
    {value: 'UG', label: 'Uganda'},
    {value: 'AE', label: 'United Arab Emirates'},
    {value: 'GB', label: 'United Kingdom'},
    {value: 'US', label: 'United States'},
    {value: 'UY', label: 'Uruguay'},
    {value: 'VU', label: 'Vanuatu'},
    {value: 'VA', label: 'Vatican'},
    {value: 'VN', label: 'Vietnam'},
    {value: 'WF', label: 'Wallis and Futuna'},
    {value: 'ZM', label: 'Zambia'},
    {value: 'ZW', label: 'Zimbabwe'}
]

export const RECURRING_PROCESSING_MODEL = {
    CARD_ON_FILE: 'CardOnFile'
}

export const DYNAMIC_IMAGE_WIDTHS = {
    full: [320, 768, 1200, 1440, 1920, 2520],
    half: [160, 384, 600, 720, 960, 1260],
    third: [106.67, 256, 400, 480, 640, 840],
    quarter: [80, 192, 300, 360, 480, 630],
    halfandfullonsmalldevice: [320, 768, 768, 720, 960, 1260],
    thirdandfullonsmalldevice: [320, 768, 768, 480, 640, 840],
    quarterandfullonsmalldevice: [320, 768, 768, 360, 480, 630]
}

export const TURKEY_PARTNER_SITE = 'https://www.vilebrequin.com.tr'
export const RUSSIA_PARTNER_SITE = 'https://vilebrequin.ru'

//Constants for Size Guide on PDPs

export const EUROPEAN_COUNTRIES_SIZE_GUIDE = [
    'FR',
    'DE',
    'ES',
    'NL',
    'BE',
    'SE',
    'NO',
    'FI',
    'DK',
    'PT',
    'GR'
]
export const INCH_COUNTRIES_SIZE_GUIDE = ['USA', 'UK']

//End Constants for Size Guide on PDPs

// Constants for Gift Certificates
export const GC_ACTION_ERROR_TYPE = {
    API_ERROR: 'API_ERROR',
    GC_ALREADY_IN_USE: 'GC_ALREADY_IN_USE',
    GC_CURRENCY_NOT_MATCH: 'GC_CURRENCY_NOT_MATCH',
    GC_EXPIRED: 'GC_EXPIRED',
    GC_NO_BALANCE: 'GC_NO_BALANCE',
    GC_NOT_EXIST: 'GC_NOT_EXIST'
}
export const GIFTCARD_PDC_PAGE_ID = 'giftpage'

export const SHIPPING_METHOD = {
    VIRTUAL: 'virtualShippingMethod'
}

// Constants for menu
export const CAT_MENU_DEFAULT_NAV_SSR_DEPTH = 3

export const MAX_CACHE_AGE_STATIC_PAGES = 60 * 60 * 24 // 1 day

export const API_ERROR_MESSAGE = defineMessage({
    id: 'global.error.something_went_wrong',
    defaultMessage: 'Something went wrong'
})

export const API_ERROR_TRY_MESSAGE = defineMessage({
    id: 'global.error.try_message',
    defaultMessage: 'Something went wrong. Try again.'
})

// Constants for Cart
export const CART_PDC_PAGE_ID = 'cart'

export const CONTACT_PHONE_NUMBERS = {
    eu: {
        fr: '+33 182 88 18 13',
        en: '+44 133 240 80 04',
        de: '+49 69 96 75 87 28',
        it: '+39 02 60 06 30 42',
        es: '+44 133 240 80 04'
    },
    us: {
        en: '+1(888)458-0051',
        es: '+1(888)458-0051'
    },
    'outlet-eu': {
        fr: '+33 182 88 18 13',
        en: '+44 133 240 80 04',
        de: '+49 69 96 75 87 28',
        it: '+39 02 60 06 30 42',
        es: '+44 133 240 80 04'
    },
    'outlet-us': {
        en: '+1(888)458-0051',
        es: '+1(888)458-0051'
    }
}

export const MODELS_NO_GIFT = [
    'COPP',
    '24BOTTLE',
    'HAMAC',
    'CLAY',
    'BAGSU',
    'BAGSIB',
    'SUNNY',
    'LIEONSUN',
    'CLARA',
    'BRITBAG',
    'GUILLIAN',
    'BARLIN',
    'BAG-48',
    'BAG-72',
    'BABETH',
    'FUNNY',
    'CHARMING',
    'BAMBOO',
    'BAGMU',
    'CLAUDIO',
    'BRETBAG',
    'AMAYA',
    'CHERRY',
    'CLAUDIA',
    'GIFT',
    'CLAP',
    'PACKMU',
    'ALMANAC',
    'SHARKMIX',
    'WOORIDE',
    'BLV23',
    'STARFISH',
    'LOBSTER',
    'LOUNGE',
    'SHELL'
]
// Regular expression to allow Latin characters, punctuation, numbers, and specific symbols
// !!! Important: not allow < or >
export const SPECIAL_REGEX_1 = /^[\w\s.,!?;:()'"\-+@#&%/=ñÑáéíóúÁÉÍÓÚüçÇàèìòùÀÈÌÒÙäëïöÄËÏÖÜß*]+$/u
export const BREADCRUMB_HOME_CATEGORY_NAME = 'Vilebrequin'

//Pages
export const COOKIE_POLICY_URL = '/page/cookiespolicy'
export const PRIVACY_POLICY_URL = '/page/privacypolicy'
export const CONTACT_US_URL = '/page/contactus'
export const FAQ_URL = '/page/faq'
export const LOGIN_URL = '/login'
export const ACCOUNT_ORDERS_URL = '/account/orders'
export const TRACK_MY_ORDER_URL = '/page/trackmyorder'
export const TERMS_AND_CONDITIONS_URL = '/page/termsandconditions'
export const LEGAL_MENTIONS_URL = '/page/legalnotices'
export const ACCESSIBILITY_URL = '/page/accessibiltystatement'

// sessionStorage items
export const HEADER_BANNER_STORAGE_NAME = 'TOP_BANNER_STATE'

//Stores
export const CLIENT_DEFAULT_LOCATION_EU = {latitude: 48.866667, longitude: 2.333333}
export const CLIENT_DEFAULT_LOCATION_US = {latitude: 40.3, longitude: -71.51}

// Variation Attributes
export const COLOR_ATTRIBUTE_ID = 'color'
export const SIZE_ATTRIBUTE_ID = 'size'

// Product Attributes
export const CODE_MARCHE_FEM = 'FEM'
export const TYPE_PROD_BAS = 'BAS'
export const TYPE_PROD_HAUT = 'HAUT'
export const LIGNE_PROD_SHO = 'SHO'
export const LIGNE_PROD_RSH = 'RSH'
export const LIGNE_PROD_MDB = 'MDB'
export const LIGNE_PROD_CHA = 'CHA'
export const LIGNE_PROD_POL = 'POL'
export const LIGNE_PROD_CHE = 'CHE'
export const CODE_MARCHE_GAR = 'GAR'
export const CODE_MARCHE_FIL = 'FIL'
export const CODE_MARCHE_HOM = 'HOM'
export const CODE_FORM_MDB = 'MDB'
export const CODE_FORM_PAP = 'PAP'
export const CODE_FORM_ACC = 'ACC'
export const IS_FAMILY_MD = 'MD'
export const IS_FAMILY_FS = 'FS'
export const IS_FAMILY_F = 'F'
export const IS_FAMILY_NO = 'No'
export const RESEAU_H = 'H'

// Size Guide Related
export const SIZE_GUIDE_FOLDER_PATH =
    '/on/demandware.static/-/Library-Sites-VBQ-shared-library/default/sizeguide/'
export const DEFAULT_SIZE_GUIDE_LANGUAGE = 'EN'

// Category IDs
export const CATEGORY_ID_WOMEN_SWIMWEAR = 'women-swimwear'
export const CATEGORY_ID_WOMEN_SWIMWEAR_BIKINIS = 'women-swimwear-bikinis'

// Checkout
export const CHECKOUT_PDC_PAGE_ID = 'checkout'

// Newsletter subscription modal
export const NEWSLETTER_SUBSCRIPTION_MODAL_PAGE_ID = 'newsletter-subscription-modal'

export const DEFAULT_SHIPMENT_ID = 'me'
export const GC_SHIPMENT_ID = 'gc'

// File Extensions
export const PDF_EXTENSION = '.pdf'

// Styleguides
export const STYLEGUIDE_MEN_PAGE_ID = 'style-guide-men'
export const STYLEGUIDE_WOMEN_PAGE_ID = 'style-guide-women'
export const CODE_MARCHE_MEN = 'HOM'
export const CODE_MARCHE_WOMEN = 'FEM'

// Shipping statuses
export const SHIPPING_STATUSES = defineMessages({
    NOTSHIPPED: {
        id: 'account_order_detail.shipping_status.not_shipped',
        defaultMessage: 'Being processed'
    },
    PARTSHIPPED: {
        id: 'account_order_detail.shipping_status.part_shipped',
        defaultMessage: 'Partially shipped'
    },
    SHIPPED: {
        id: 'account_order_detail.shipping_status.shipped',
        defaultMessage: 'Shipped'
    }
})

export const STORE_STATUSES = defineMessages({
    OPEN: {
        id: 'store_status.open',
        defaultMessage: 'Open'
    },
    CLOSED: {
        id: 'store_status.closed',
        defaultMessage: 'Closed'
    },
    TEMPORARILY_CLOSED: {
        id: 'store_status.temporarily_closed',
        defaultMessage: 'Temporarily Closed'
    }
})

// Order statuses
export const ORDER_STATUSES = defineMessages({
    completed: {
        id: 'account_order_detail.status.completed',
        defaultMessage: 'Completed'
    },
    cancelled: {
        id: 'account_order_detail.status.cancelled',
        defaultMessage: 'Cancelled'
    },
    failed: {
        id: 'account_order_detail.status.failed',
        defaultMessage: 'Failed'
    }
})

export const CARD_ARIA_LABEL = defineMessages({
    PRODUCT: {
        id: 'card.product.aria.label',
        defaultMessage: 'product'
    },
    GIFT_CARD: {
        id: 'card.gift.card.aria.label',
        defaultMessage: 'gift card'
    }
})

export const DEFAULT_SHIPPING_STATUS = 'NOTSHIPPED'

// AGEC Related CONSTANTS
export const AGEC_ELIGIBLE_LIGNE_PROD = [
    'VES',
    'UDW',
    'JUP',
    'BLA',
    'BON',
    'PON',
    'ECH',
    'ROB',
    'PLA',
    'MDB',
    'BER',
    'CHE',
    'PAN',
    'TEE',
    'PAR',
    'CAS',
    'PW',
    'SER',
    'SWE',
    'CAL',
    'POL',
    'CHA',
    'PUL',
    'BLO',
    'CEI',
    'CHP',
    'GIL',
    'COU',
    'BOX',
    'SHO',
    'DEB',
    'FOU',
    'ACO',
    'TRO',
    'GWP',
    'COV',
    'PEI',
    'RCH',
    'RPO',
    'CAR',
    'SOU',
    'RSH',
    'SAL',
    'SAD',
    'ACS',
    'PTE',
    'JEA',
    'COM',
    'HEA',
    'FIL',
    'MAK',
    'DOG',
    'CSS',
    'OCC',
    'SAS',
    'LGM',
    'LGR',
    'MTA',
    'CAD',
    'SHR',
    'CAG',
    'DPL',
    'GNT',
    'MOF',
    'VNY',
    'TOP',
    'BSR',
    'BOD',
    'CUL',
    'KIM',
    'JSH',
    'BAB',
    'KAF',
    'CHS',
    'TOD',
    'PYJ'
]

export const DEFAULT_LOCALE_DISPATCH_URL = 'en-GB'

export const RECAPTCHA_SITE_KEY = '6LeftxQnAAAAACwmOQvZ6eAyrRByr0TpzePsIlIl'

export const CART_HREF = '/cart'
export const HOME_HREF = '/home'
export const GIFTCARD_HREF = '/giftcard'
export const PRODUCT_SEARCH_VARIATION_GROUP_REFINEMENT = 'htype=variation_group'
export const PRODUCT_SEARCH_PRODUCT_TYPE_REFINEMENT = 'htype=variation_group|set'
// GTM
export const GTM_SCRIPT_URL = 'https://metrics.vilebrequin.com/usac4iv9m0gqnce.js?id='
export const GTM_NO_SCRIPT_URL = 'https://metrics.vilebrequin.com/ns.html?id='

// Newsletter modal open time in milliseconds
export const NEWSLETTER_MODAL_OPEN_TIME = 30000

// Outlet sites
export const OUTLET_ENTRANCE_URL = '/entrance'
export const OUTLET_DISPATCH_URL = '/dispatch'
export const OUTLET_LIMITATION_EXCEPTIONS = [
    OUTLET_ENTRANCE_URL,
    PRIVACY_POLICY_URL,
    FAQ_URL,
    LEGAL_MENTIONS_URL,
    '/account',
    '/registration',
    TRACK_MY_ORDER_URL,
    LOGIN_URL
]
export const OUTLET_SESSION_PARAM = 'outletLimitationStatus'
export const UNLIMITED_OUTLET_SESSION = 'unlimited'
export const LIMITED_OUTLET_SESSION = 'limited'

// 404 error page
export const PAGE_NOT_FOUND_PDC_PAGE_ID = 'page-not-found'

// Einstein recommender constants used in <RecommendedProducts/>
export const EINSTEIN_RECOMMENDERS = {
    ADD_TO_CART_MODAL: 'pdp-similar-items',
    CART_RECENTLY_VIEWED: 'viewed-recently-einstein',
    CART_MAY_ALSO_LIKE: 'product-to-product-einstein',
    PDP_COMPLETE_SET: 'complete-the-set',
    PDP_MIGHT_ALSO_LIKE: 'pdp-similar-items',
    PDP_RECENTLY_VIEWED: 'viewed-recently-einstein',
    EMPTY_SEARCH_RESULTS_TOP_SELLERS: 'home-top-revenue-for-category',
    EMPTY_SEARCH_RESULTS_MOST_VIEWED: 'products-in-all-categories',
    PAGE_NOT_FOUND: 'product-to-product-einstein'
}

// Recommendation Strategies
export const ADD_TO_CART_STRATEGIES = {
    WOMEN_BIKINI: 'women-bikinis',
    MEN_SWIMSHORTS: 'men-swimshorts',
    LIGNE_PRODUIT: 'ligne-produit',
    IS_FAMILY: 'is-family',
    DEFAULT: 'default'
}
// Product automated links constants
export const MISTRAL_PAGE_ID = 'MISTRAL'
export const MISTRAL_PAGE_PATH = '/page/mistral'
export const FAMILY_CATEGORY_PREFIX = 'family-'

// Click and collect hardcored store fist and last name
export const CLICK_AND_COLLECT_STORE_FIRST_NAME = defineMessage({
    id: 'global.cc.store_first_name',
    defaultMessage: 'Vilebrequin'
})
export const CLICK_AND_COLLECT_STORE_LAST_NAME = defineMessage({
    id: 'global.cc.store_last_name',
    defaultMessage: 'Store'
})

// Product Attributes
export const PROD_ATTR_CODE_LIGNE_PROD = 'c_orliweb_code_ligne_produit'

export const LANGUAGES_REQUIRING_LATIN_NUMERALS = ['ar', 'bn']

// Cookies
export const COOKIE = {
    USER_SET_LOCALE: 'user-set-locale',
    HEADER_BANNER_STATE: 'header-banner-state'
}

export const COLOR_REGROUPMENT_ID_WHITE = '00010'
export const COLOR_REGROUPMENT_ID_BLACK = '00120'

export const REFINEMENT_GENDER_ATTRIBUTE = 'c_orliweb_stat03_code_marche'
export const DEFAULT_HOMEPAGE_MEGAZINE = {
    name: 'Magazine Homepage'
}

// GTM Categories IDs for Item Categories
export const GTM_CATEGORIES_IDS = {
    women: {lvl2: ['women-swimwear', 'women-family', 'women-ready-to-wear', 'women-new-arrivals']},
    men: {
        lvl2: [
            'men-swimwear',
            'men-family',
            'men-ready-to-wear',
            'men-new-arrivals',
            'men-swimshorts'
        ]
    },
    kids: {lvl2: []},
    boys: {lvl2: ['boys-swimwear', 'boys-ready-to-wear', 'boys-new-in']},
    girls: {lvl2: ['girls-swimwear', 'girls-ready-to-wear', 'girls-new-in']},
    accessories: {
        lvl2: [
            'accessories-hat',
            'accessories-bags',
            'accessories-sunglasses',
            'accessories-towels',
            'accessories-pouches',
            'accessories-shoes',
            'accesories-outdoor',
            'accessories-belt',
            'accessories-jewels-and-watch',
            'accessories-keyring',
            'accessories-kids',
            'accessories-scarves',
            'accessories-socks',
            'accessories-beach-games',
            'topolino-fiat'
        ]
    }
}

export const EU_DOMESTIC_COUNTRY_CODES = [
    'IT', // ITALY
    'FR', // FRANCE
    'BE', // BELGIUM
    'DK', // DENMARK
    'ES', // SPAIN
    'FI', // FINLAND
    'GR', // GREECE
    'IE', // IRELAND
    'LU', // LUXEMBOURG
    'NL', // NETHERLANDS
    'PT', // PORTUGAL
    'SE', // SWEDEN
    'HR', // CROATIA
    'HU', // HUNGARY
    'PL', // POLAND
    'CZ', // CZECH REPUBLIC
    'SK', // SLOVAKIA
    'SI', // SLOVENIA
    'DE', // GERMANY
    'AT', // AUSTRIA
    'LV', // LATVIA
    'LT', // LITHUANIA
    'EE', // ESTONIA
    'BG', // BULGARIA
    'RO', // ROMANIA
    'MT', // MALTA
    'CY', // CYPRUS
    'MC' // MONACO
]

export const CART_ERROR_PRODUCT_NOT_AVAILABLE_QTY = defineMessage({
    id: 'cart.error.product_not_available_qty',
    defaultMessage: 'The product {productId} isn’t available for quantity {qty}.'
})

export const BASKET_ITEM_ADD_ERROR = 'BASKET_ITEM_ADD_ERROR'

export const ADDRESS_VALIDATION_ERROR_CODE = 'ADDRESS_VALIDATION'

export const TAX_CALCULATION_ERROR_CODE = 'TAX_CALCULATION'
export const TAX_CALCULATION_ERROR_MESSAGE = defineMessage({
    id: 'checkout.avatax.error',
    defaultMessage: 'Unable to compute the sales tax, please try again'
})

// GORGIAS CHAT
export const GORGIAS_CHAT_WIDGET_URL = 'https://config.gorgias.chat/bundle-loader/'
export const GORGIAS_CHAT_BUNDLE_URL = 'https://static.9gtb.com/loader.js?g_cvt_id='
export const GORGIAS_CHAT_WIDGET_KEY = {
    'VBQ-EU': {
        'en-GB': '01KJZ2GYS0GCR1QH9533SBMVE4',
        'fr-FR': '01KJZ25JGTXJYZ13F42EEX152S',
        'es-ES': '01KJZ2SMMRFH6A5XBPVHAYME38',
        'it-IT': '01KJZ3RM5D1JMM8781ERZ3S9J3',
        'de-DE': '01KJZ3MFG8K3PQ4QEBGCCDE1XZ'
    },
    'VBQ-US': {
        'en-US': '01KFXDH6KFZN5J7Z2YQ5MSZXK0',
        'es-US': '01KJZ2YB4XYMJ993H455Z4ZPMD'
    },
    'VBQ-OUTLET-EU': {
        'en-GB': '01KJZ2GYS0GCR1QH9533SBMVE4',
        'fr-FR': '01KJZ25JGTXJYZ13F42EEX152S',
        'es-ES': '01KJZ2SMMRFH6A5XBPVHAYME38',
        'it-IT': '01KJZ3RM5D1JMM8781ERZ3S9J3',
        'de-DE': '01KJZ3MFG8K3PQ4QEBGCCDE1XZ'
    },
    'VBQ-OUTLET-US': {
        'en-US': '01KFXDH6KFZN5J7Z2YQ5MSZXK0',
        'es-US': '01KJZ2YB4XYMJ993H455Z4ZPMD'
    }
}
export const GORGIAS_CHAT_BUNDLE_KEY = {
    'VBQ-EU': {
        'en-GB': 'fc0791a7-396b-431f-893e-04d2106e3be4',
        'fr-FR': '9d9631f0-6cad-48ca-9c8a-e3ba24454d0d',
        'es-ES': 'c78662aa-b86f-402d-b941-a8073c35cda9',
        'it-IT': 'abe1e7cc-d824-48c9-a419-4063e075eb36',
        'de-DE': '376dd387-e1a6-4c68-b413-def0eb2998ae'
    },
    'VBQ-US': {
        'en-US': '373ac362-d8ac-45ed-89a4-69957481df63',
        'es-US': '5a71f880-1e1f-46cf-9cd3-a97cc5719249'
    },
    'VBQ-OUTLET-EU': {
        'en-GB': 'fc0791a7-396b-431f-893e-04d2106e3be4',
        'fr-FR': '9d9631f0-6cad-48ca-9c8a-e3ba24454d0d',
        'es-ES': 'c78662aa-b86f-402d-b941-a8073c35cda9',
        'it-IT': 'abe1e7cc-d824-48c9-a419-4063e075eb36',
        'de-DE': '376dd387-e1a6-4c68-b413-def0eb2998ae'
    },
    'VBQ-OUTLET-US': {
        'en-US': '373ac362-d8ac-45ed-89a4-69957481df63',
        'es-US': '5a71f880-1e1f-46cf-9cd3-a97cc5719249'
    }
}

// GORGIAS Contact form
export const GORGIAS_CONTACT_FORM_SCRIPT_URL =
    'https://contact.gorgias.help/api/contact-forms/loader.js?v=3'

export const GORGIAS_FORM_UIDS = {
    'VBQ-EU': {
        'en-GB': 'hvrd13rl',
        'fr-FR': 'jdhtbo7u',
        'es-ES': '2zorsbl6',
        'it-IT': '2n8v0yck',
        'de-DE': 'fdtfrc7a'
    },
    'VBQ-US': {
        'en-US': '1hyclxtu',
        'es-US': 'i2qzvyju'
    },
    'VBQ-OUTLET-EU': {
        'en_GB': '5kcejyp9',
        'fr-FR': 'sc9gq3b4',
        'es-ES': 'aa4klebo',
        'it-IT': 'umj4p5wf',
        'de-DE': 'tm7fw6lv'
    },
    'VBQ-OUTLET-US': {
        'en-US': 'z3wsmtq8',
        'es-US': '66zryjam'
    }    
}

// default, for en-GB, en-US
export const DEFAULT_GORGIAS_FORM_UID = 'hvrd13rl'
